export type LoanStage =
  | 'Application Taken'
  | 'Docs Out'
  | 'Docs Signed'
  | 'Processing'
export type UrgencyLevel = 'Critical' | 'High' | 'Medium'

export type CallOutcome =
  | 'Connected'
  | 'Left Voicemail'
  | 'No Answer'
  | 'Bad Number'

export interface LoanNote {
  id: string
  author: string
  body: string
  timestamp: string
}

export interface Loan {
  id: string
  borrowerName: string
  amount: number
  stage: LoanStage
  assignedLO: string
  timeSinceLastCall: number
  timeInStage: number
  state: string
  urgency: UrgencyLevel
  callAttempts: number
  enteredSharkTankAt: string
  isDialReady: boolean
  lastCallOutcome?: CallOutcome | null
  lastCallAt?: string
  notes: LoanNote[]
}

export type ClaimStatus = 'pending' | 'funded'

export interface ClaimedLoan extends Loan {
  claimedAt: string
  status: ClaimStatus
  fundedAt?: string
}

export type ActivityAction = 'Claimed' | 'Called' | 'Funded' | 'Released'

export interface Activity {
  id: string
  loName: string
  borrowerName: string
  action: ActivityAction
  timestamp: string
  amount: number
}

export interface LeaderboardEntry {
  loName: string
  points: number
  fundedCount: number
  fundedVolume: number
  pendingCount: number
}

export interface RankTier {
  id: string
  name: string
  minPoints: number
}

export interface PointsConfig {
  basePoints: number // points awarded just for funding
  pointsPerThousand: number // 1 = +1 pt per $1k funded; default 0.1 (= 1 pt / $10k)
  tiers: RankTier[]
}

export const defaultPointsConfig: PointsConfig = {
  basePoints: 100,
  pointsPerThousand: 0.1, // = 1 pt per $10k funded (matches original formula)
  tiers: [
    { id: 't1', name: 'Rookie', minPoints: 0 },
    { id: 't2', name: 'Hunter', minPoints: 500 },
    { id: 't3', name: 'Predator', minPoints: 1500 },
    { id: 't4', name: 'Apex Shark', minPoints: 3000 },
  ],
}

export function pointsForFunding(
  amount: number,
  cfg: PointsConfig = defaultPointsConfig,
): number {
  return cfg.basePoints + Math.floor((amount / 1000) * cfg.pointsPerThousand)
}

export function tierForPoints(
  points: number,
  cfg: PointsConfig = defaultPointsConfig,
): RankTier {
  const sorted = [...cfg.tiers].sort((a, b) => b.minPoints - a.minPoints)
  return sorted.find((t) => points >= t.minPoints) ?? cfg.tiers[0]
}
